import { Factory } from 'fishery';
import faker from 'faker';

import { FlexiblePaymentFrequency } from '../GetPaymentDetails';
import {
  ServerResponseType,
  ServerOptionsType,
  CoverageType,
  ServerOption,
  PaymentFrequenciesType,
} from './types';

type SelectOption<T> = {
  label: string;
  value: T;
};

type PaymentFrequencyTransientParams = {
  name: FlexiblePaymentFrequency;
  options: Array<SelectOption<string>>;
};

type OptionsTransientParams = {
  field: string;
};

const optionsFactory = Factory.define<ServerOption, OptionsTransientParams>(
  ({ sequence, transientParams }) => ({
    label: `${transientParams.field}-label-${sequence}`,
    value: `${transientParams.field}-value-${sequence}`,
  })
);

const coverageConfigFactory = Factory.define<
  CoverageType<ServerOptionsType>,
  OptionsTransientParams
>(({ transientParams }) => ({
  coverageType: transientParams.field || '',
  coverageLabel: `${transientParams.field}-label`,
  coverageLevels: optionsFactory.buildList(2, undefined, {
    transient: transientParams,
  }),
  coverageDescription: {
    summary: `${transientParams.field}-summary`,
    definition: `${transientParams.field}-definition`,
    requirement: `${transientParams.field}-requirement`,
  },
  signatureRequiredToWaive: null,
  signatureRequiredToInclude: null,

  name: `${transientParams.field}-label`, // Merged from dictionary coverages
}));

export const getConfigResponseFactory = Factory.define<
  ServerResponseType,
  {
    allowExcludedDrivers: boolean;
    accessEnabled?: boolean;
    predictableProtectionFeeApplicable?: boolean;
  }
>(({ transientParams }) => ({
  insurableParty: {
    allowExcludedDrivers:
      transientParams.allowExcludedDrivers !== undefined
        ? transientParams.allowExcludedDrivers
        : true,
    coverageHistory: {
      coveredWithinLookbackPeriod: {
        required: transientParams.accessEnabled ?? false,
        options: optionsFactory.buildList(2, undefined, {
          transient: { field: 'coveredWithinLookbackPeriod' },
        }),
      },
    },
    accidents: {
      challengeReasonOptions: optionsFactory.buildList(2, undefined, {
        transient: { field: 'challengeReasonOptions' },
      }),
      collectLossInformation: true,
      lossOptions: optionsFactory.buildList(2, undefined, {
        transient: { field: 'lossOptions' },
      }),
      collectAccidentType: true,
      accidentTypeOptions: optionsFactory.buildList(2, undefined, {
        transient: { field: 'accidentTypeOptions' },
      }),
      lookbackYears: faker.datatype.number(),
      day: optionsFactory.buildList(2, undefined, {
        transient: { field: 'day' },
      }),
      month: optionsFactory.buildList(2, undefined, {
        transient: { field: 'month' },
      }),
      year: optionsFactory.buildList(2, undefined, {
        transient: { field: 'year' },
      }),
      collectDayOfAccident: true,
      collectTypeOfLoss: false,
    },
    currentInsurance: {
      biLevel: optionsFactory.buildList(2, undefined, {
        transient: { field: 'biLevel' },
      }),
      insuredDuration: optionsFactory.buildList(2, undefined, {
        transient: { field: 'insuredDuration' },
      }),
      carrierName: optionsFactory.buildList(2, undefined, {
        transient: { field: 'carrierName' },
      }),
      lapseDuration: optionsFactory.buildList(2, undefined, {
        transient: { field: 'lapseDuration' },
      }),
      lapseReason: optionsFactory.buildList(2, undefined, {
        transient: { field: 'lapseReason' },
      }),
      askForCurrentPolicy: true,
    },
    driverLicense: {
      state: optionsFactory.buildList(2, undefined, {
        transient: { field: 'state' },
      }),
      status: optionsFactory.buildList(2, undefined, {
        transient: { field: 'status' },
      }),
      verified: {
        required: false,
      },
      hasForeignLicense: {
        required: false,
      },
      hasLearnersPermit: {
        required: false,
      },
      reportStatus: optionsFactory.buildList(2, undefined, {
        transient: { field: 'reportStatus' },
      }),
    },
    stateResidency: {
      required: false,
    },
    drivingHistory: {
      previousSuspensionCount: optionsFactory.buildList(2, undefined, {
        transient: { field: 'previousSuspensionCount' },
      }),
      previousSuspensionDate: optionsFactory.buildList(2, undefined, {
        transient: { field: 'previousSuspensionDate' },
      }),
    },
    education: {
      askEducation: true,
      currentLevel: optionsFactory.buildList(2, undefined, {
        transient: { field: 'currentLevel' },
      }),
    },
    exclusionReason: optionsFactory.buildList(2, undefined, {
      transient: { field: 'exclusionReason' },
    }),
    maxDrivers: faker.datatype.number(7),
    militaryService: {
      militaryRank: optionsFactory.buildList(2, undefined, {
        transient: { field: 'militaryRank' },
      }),
      serviceType: optionsFactory.buildList(4, undefined, {
        transient: { field: 'militaryServiceType' },
      }),
      askMilitaryService: true,
    },
    personalInfo: {
      gender: optionsFactory.buildList(2, undefined, {
        transient: { field: 'gender' },
      }),
      maritalStatus: optionsFactory.buildList(2, undefined, {
        transient: { field: 'maritalStatus' },
      }),
      relationshipToApplicant: optionsFactory.buildList(2, undefined, {
        transient: { field: 'relationshipToApplicant' },
      }),
      suffix: optionsFactory.buildList(2, undefined, {
        transient: { field: 'suffix' },
      }),
      ssn: {
        is_optional: false,
      },
    },
    profession: optionsFactory.buildList(2, undefined, {
      transient: { field: 'profession' },
    }),
    askProfession: true,
    violations: {
      lookbackYears: faker.datatype.number(),
      month: optionsFactory.buildList(2, undefined, {
        transient: { field: 'month' },
      }),
      day: optionsFactory.buildList(2, undefined, {
        transient: { field: 'day' },
      }),
      violationType: optionsFactory.buildList(2, undefined, {
        transient: { field: 'violationType' },
      }),
      year: optionsFactory.buildList(2, undefined, {
        transient: { field: 'year' },
      }),
      challengeReasonOptions: optionsFactory.buildList(2, undefined, {
        transient: { field: 'challengeReasonOptions' },
      }),
      askForSr22: true,
    },
    removalReason: optionsFactory.buildList(2, undefined, {
      transient: { field: 'removalReason' },
    }),
    goodStudentDiscount: {
      enabled: false,
      mustBeUnmarried: null,
      mustBeYoungerThan: null,
    },
    typeOfLoss: optionsFactory.buildList(2, undefined, {
      transient: { field: 'theft' },
    }),
    statusDetails: {
      hasAccessToVehicles: {
        requiredForRemoval: true,
      },
      livesInHousehold: {
        requiredForRemoval: true,
      },
      hasOwnInsurancePolicy: {
        requiredForRemoval: true,
      },
    },
    cfrs: {
      askForCfrs: true,
      defaultIssueState: 'FL',
    },
    priorPolicies: {
      required: false,
    },
  },
  insurableItem: {
    usage: optionsFactory.buildList(2, undefined, {
      transient: { field: 'vehicle-usage' },
    }),
    ownership: {
      askRegisteredToApplicant: true,
      ownershipType: optionsFactory.buildList(2, undefined, {
        transient: { field: 'vehicle-ownership-type' },
      }),
    },
    parkingDetails: {
      inStateParkingDuration: {
        required: false,
        options: optionsFactory.buildList(2, undefined, {
          transient: { field: 'vehicle-parking-duration' },
        }),
      },
      parkingLocationReason: {
        required: false,
        options: optionsFactory.buildList(2, undefined, {
          transient: { field: 'vehicle-parking-location-reason' },
        }),
      },
    },
    parkingType: {
      required: false,
      options: optionsFactory.buildList(2, undefined, {
        transient: { field: 'vehicle-parking-type' },
      }),
    },
    rideshareUse: {
      required: true,
    },
    usageDetails: {
      required: false,
    },
    mileage: {
      required: true,
    },
    businessUse: {
      required: false,
    },
    businessUseType: optionsFactory.buildList(2, undefined, {
      transient: { field: 'vehicle-business-use-type' },
    }),
    lienholders: [
      {
        name: { label: 'name label', value: 'name' },
        address: {
          addressLine1: 'addressLine1',
          addressLine2: 'addressLine2',
          city: 'city',
          postalCode: 'postalCode',
          state: 'state',
        },
      },
    ],
  },
  agencyInformation: {
    agencyBundle: {
      carrier: [
        {
          label: '--Carrier 1--',
          value: '--carrier-value-1--',
        },
        {
          label: '--Carrier 2--',
          value: '--carrier-value-2--',
        },
      ],
      policyType: [
        {
          label: 'None',
          value: 'none',
        },
        {
          label: '--Policy Type 1--',
          value: '--policy-type-value-1--',
        },
        {
          label: '--Policy Type 2--',
          value: '--policy-type-value-2--',
        },
      ],
    },
  },
  coverages: {
    insuranceProduct: [
      coverageConfigFactory.build(
        {},
        { transient: { field: 'bodily_injury' } }
      ),
      coverageConfigFactory.build(
        {},
        { transient: { field: 'medical_payments' } }
      ),
      coverageConfigFactory.build({}, { transient: { field: 'limited_tort' } }),
    ],
    insurableItem: [
      coverageConfigFactory.build({}, { transient: { field: 'collision' } }),
      coverageConfigFactory.build(
        {},
        { transient: { field: 'comprehensive' } }
      ),
      coverageConfigFactory.build({}, { transient: { field: 'rideshare' } }),
    ],
  },
  drivers: {
    // Todo: clean this up and handle correctly in drivers list tests
    personalInfo: {
      suffix: {
        none: '',
        Jr: 'Jr',
        Sr: 'Sr',
        I: 'I',
        II: 'II',
        III: 'III',
        IV: 'IV',
      },
    },
  },
  effectiveDates: {
    startDate: '2023-08-01',
    endDate: '2023-12-01',
  },
  telematics: {
    enabled: false,
    required: false,
    enrollmentWindow: 10,
  },
  flexiblePayments: {
    enabled: false,
    paymentFrequencies: [
      createBiweeklyPaymentFrequencyConfig(),
      createMonthlyPaymentFrequencyConfig(),
      createSemiMonthlyPaymentFrequencyConfig(),
      createWeeklyPaymentFrequencyConfig(),
      { name: 'fullPay', options: [] },
    ],
  },
  accessEnabled: !!transientParams.accessEnabled,
  predictableProtectionFeeApplicable: !!transientParams.predictableProtectionFeeApplicable,
}));

export const paymentFrequencyFactory = Factory.define<
  PaymentFrequenciesType[number],
  PaymentFrequencyTransientParams
>(({ transientParams }) => ({
  name: transientParams.name || 'weekly',
  options: transientParams.options || [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ],
}));

export const createWeeklyPaymentFrequencyConfig = () =>
  paymentFrequencyFactory.build({
    name: 'weekly',
    options: [
      { value: 'monday', label: 'Monday' },
      { value: 'tuesday', label: 'Tuesday' },
      { value: 'wednesday', label: 'Wednesday' },
      { value: 'thursday', label: 'Thursday' },
      { value: 'friday', label: 'Friday' },
      { value: 'saturday', label: 'Saturday' },
      { value: 'sunday', label: 'Sunday' },
    ],
  });

export const createBiweeklyPaymentFrequencyConfig = () =>
  paymentFrequencyFactory.build({
    name: 'biWeekly',
    options: [
      { value: 'monday', label: 'Monday' },
      { value: 'tuesday', label: 'Tuesday' },
      { value: 'wednesday', label: 'Wednesday' },
      { value: 'thursday', label: 'Thursday' },
      { value: 'friday', label: 'Friday' },
      { value: 'saturday', label: 'Saturday' },
      { value: 'sunday', label: 'Sunday' },
    ],
  });

export const createSemiMonthlyPaymentFrequencyConfig = () =>
  paymentFrequencyFactory.build({
    name: 'semiMonthly',
    options: [
      { value: [1, 15], label: '1st and 15th' },
      { value: [2, 16], label: '2nd and 16th' },
      { value: [3, 17], label: '3rd and 17th' },
      { value: [4, 18], label: '4th and 18th' },
      { value: [5, 19], label: '5th and 19th' },
      { value: [6, 20], label: '6th and 20th' },
      { value: [7, 21], label: '7th and 21st' },
      { value: [8, 22], label: '8th and 22nd' },
      { value: [9, 23], label: '9th and 23rd' },
      { value: [10, 24], label: '10th and 24th' },
      { value: [11, 25], label: '11th and 25th' },
      { value: [12, 26], label: '12th and 26th' },
      { value: [13, 27], label: '13th and 27th' },
      { value: [14, 28], label: '14th and 28th' },
    ],
  });

export const createMonthlyPaymentFrequencyConfig = () =>
  paymentFrequencyFactory.build({
    name: 'monthly',
    options: [
      { label: '1st', value: 1 },
      { label: '2nd', value: 2 },
      { label: '3rd', value: 3 },
      { label: '4th', value: 4 },
      { label: '5th', value: 5 },
      { label: '6th', value: 6 },
      { label: '7th', value: 7 },
      { label: '8th', value: 8 },
      { label: '9th', value: 9 },
      { label: '10th', value: 10 },
      { label: '11th', value: 11 },
      { label: '12th', value: 12 },
      { label: '13th', value: 13 },
      { label: '14th', value: 14 },
      { label: '15th', value: 15 },
      { label: '16th', value: 16 },
      { label: '17th', value: 17 },
      { label: '18th', value: 18 },
      { label: '19th', value: 19 },
      { label: '20th', value: 20 },
      { label: '21st', value: 21 },
      { label: '22nd', value: 22 },
      { label: '23rd', value: 23 },
      { label: '24th', value: 24 },
      { label: '25th', value: 25 },
      { label: '26th', value: 26 },
      { label: '27th', value: 27 },
      { label: '28th', value: 28 },
      { label: '29th', value: 29 },
      { label: '30th', value: 30 },
    ],
  });
